import type { Cell } from '../api/types'
import { displayValue } from '../lib/format'
import {
  type Column, type SensorFamily, type SensorGroup, cellNote, otherSummary,
  quantityLabel, selectionKey, sensorColumns, showsAsTiles, toSensorRows,
} from '../lib/instruments'
import { isSuspect, names } from '../lib/quality'
import { formatDateTime, relativeAge } from '../lib/time'
import type { Freshness } from '../lib/freshness'
import { stalenessLabel } from './LatestReadings'

/** A receipt time judged against the station's expected interval and silent
 *  threshold (lib/freshness.ts), or null where the API did not give them. */
export type Judge = (at: string | null) => Freshness | null

const FRESH_WORD: Record<Freshness, string> = {
  ok: 'On time', late: 'Late', over: 'Overdue', never: 'No reading',
}

interface Props {
  family: SensorFamily
  judge?: Judge
  /** The monitor's silent threshold, so "reporting" agrees with the colours. */
  silentAfterS?: number
  /** More than one sensor ticked: rows name their sensor and carry their
   *  own receipt time. */
  several: boolean
  pollIntervalS: number
  selected: Set<string>
  onToggle: (key: string) => void
}

/** Link telemetry is diagnostic, and with several sensors ticked it is most
 *  of the page. It starts folded; one click opens it. */
function foldedByDefault(family: string | null): boolean {
  return family?.startsWith('link:') ?? false
}

function received(at: string | null, pollIntervalS: number, silentAfterS?: number): string {
  return at
    ? `${relativeAge(at)} · ${stalenessLabel(at, pollIntervalS, new Date(), silentAfterS)}`
    : 'no readings'
}

/** One type of reading, as the gateway's own node page shows it, for every
 *  ticked sensor at once: one table per column group, a row per sensor
 *  channel, every value with its unit and, where it is older than its
 *  sensor's family, its own age (spec §8.1, §8.2). A numeric column's
 *  header is the switch that charts it, on every ticked sensor. A group of
 *  one row of one sensor is shown as tiles instead, which is the same
 *  values and the same switches, without a one-line table to read across.
 *
 *  With one sensor the section says when its family arrived, as it always
 *  did. With several, sensors report on their own cadence and no one
 *  receipt time is true of the table, so each row carries its own. */
export function FamilySection({ family, several, pollIntervalS, selected,
                                onToggle, judge, silentAfterS }: Props) {
  // With one sensor the family's receipt time is the Last Observation, so it
  // carries the freshness word beside it (D2).
  const fresh = !several && judge ? judge(family.receivedAt) : null
  return (
    <details className="family fold" open={!foldedByDefault(family.family)}>
      <summary>
        <h3>{family.label}</h3>
        <span className="muted">
          {several
            ? `${family.sensors} ${family.sensors === 1 ? 'sensor' : 'sensors'}`
            : family.receivedAt
              ? `received ${formatDateTime(family.receivedAt)} · `
                + received(family.receivedAt, pollIntervalS, silentAfterS)
              : 'no readings'}
        </span>
        {fresh && <span className={`fresh-word fresh-${fresh} word-${fresh}`}>{FRESH_WORD[fresh]}</span>}
      </summary>
      {family.groups.map((group) => (
        showsAsTiles(group, several)
          ? <GroupTiles key={group.group ?? ''} family={family} group={group}
                        pollIntervalS={pollIntervalS}
                        selected={selected} onToggle={onToggle} />
          : <GroupTable key={group.group ?? ''} family={family} group={group}
                        several={several} pollIntervalS={pollIntervalS}
                        selected={selected} onToggle={onToggle} judge={judge}
                        silentAfterS={silentAfterS} />
      ))}
    </details>
  )
}

/** Everything a node says about itself rather than about what it measures —
 *  its health, its reading errors, the GNSS fix quality, the radio link —
 *  folded under one heading below the readings, each in its own section.
 *  The summary names what is inside, so a reader can tell whether to open
 *  it without opening it. */
export function OtherSection({ families, judge: _judge, ...rest }: Omit<Props, 'family'> & {
  families: SensorFamily[]
}) {
  // No freshness verdict here: a node's reading errors arrive only when
  // something is wrong, so their silence is the healthy state.
  void _judge
  if (!families.length) return null
  return (
    <details className="other fold">
      <summary>
        <h2>Other</h2>
        <span className="muted">{otherSummary(families)}</span>
      </summary>
      <div className="other-body">
        {families.map((family) => (
          <FamilySection key={family.key} family={family} {...rest} />
        ))}
      </div>
    </details>
  )
}

function GroupHeading({ group }: { group: SensorGroup }) {
  return group.group ? <h4>{group.group}</h4> : null
}

function GroupTiles({ family, group, pollIntervalS, selected, onToggle }:
  Omit<Props, 'several'> & { group: SensorGroup }) {
  const columns = sensorColumns(group)
  const [row] = toSensorRows(group, columns)
  if (!row) return null
  return (
    <div className="family-group">
      <GroupHeading group={group} />
      <div className="tiles-frame">
        <ul className="tiles" aria-label={group.group ?? family.label}>
          {columns.map((col, i) => (
            <li key={col.quantity}
                className={isProse(row.cells[i] ?? null) ? 'tile-wide' : undefined}>
              <Tile cell={row.cells[i] ?? null} column={col}
                    k={selectionKey(family.family, group.group, col.quantity)}
                    receivedAt={row.receivedAt} pollIntervalS={pollIntervalS}
                    selected={selected} onToggle={onToggle} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/** Text longer than a word or two: a gateway's error message, or a message
 *  type's long name, wants the width to be read; a status word does not. */
function isProse(cell: Cell | null): boolean {
  return cell?.value_type === 'text' && String(cell.value ?? '').length > 16
}

/** One value: what it is, the value at its recorded precision with its
 *  unit, and beneath it anything the reader must know before trusting it —
 *  its own age when older than its family, its quality flags. A numeric
 *  tile is the chart switch; a text tile is prose and has nothing to chart. */
function Tile({ cell, column, k, receivedAt, pollIntervalS, selected, onToggle }: {
  cell: Cell | null; column: Column; k: string; receivedAt: string | null
  pollIntervalS: number; selected: Set<string>; onToggle: (key: string) => void
}) {
  const note = cell ? cellNote(cell, receivedAt, pollIntervalS) : 'no readings'
  const flags = cell?.quality != null && isSuspect(cell.quality)
    ? names(cell.quality).join(', ') : null
  const unit = column.unit ?? cell?.unit ?? ''
  const label = quantityLabel(column.quantity)
  const value = typeof cell?.value === 'number' ? cell.value : null
  const shown = displayValue(value, cell?.precision ?? null)
  const body = (
    <>
      <span className="tile-label">{label}</span>
      {cell?.value_type === 'text' ? (
        <span className="tile-text">{cell.value ?? '—'}</span>
      ) : (
        <span className={shown.length > 10 ? 'tile-value long' : 'tile-value'}
              title={value === null ? undefined : String(value)}>
          {shown}
          {unit && <span className="tile-unit">{unit}</span>}
        </span>
      )}
      {note && <span className="tile-note">{note}</span>}
      {flags && <span className="tile-note">{flags}</span>}
    </>
  )
  if (!column.numeric) return <div className="tile">{body}</div>
  const on = selected.has(k)
  return (
    <button type="button" className="tile tile-toggle" aria-pressed={on}
            title={on ? `Remove ${label} from the charts` : `Chart ${label}`}
            onClick={() => onToggle(k)}>
      {body}
    </button>
  )
}

function GroupTable({ family, group, several, pollIntervalS, selected, onToggle, judge,
                      silentAfterS }:
  Props & { group: SensorGroup }) {
  const columns = sensorColumns(group)
  const rows = toSensorRows(group, columns)
  const labelled = rows.some((r) => r.label)
  const keys = columns.map((col) => selectionKey(family.family, group.group, col.quantity))
  return (
    <div className="family-group">
      <GroupHeading group={group} />
      {keys.length > 0 && columns.some((col) => col.numeric) && (
        <p className="table-hint muted">Click a column heading to chart it.</p>
      )}
      <div className="live-scroll">
        <table className="grid channel-table">
          <thead>
            <tr>
              {several && <th scope="col">Sensor</th>}
              {labelled && <th scope="col">Channel</th>}
              {columns.map((col, i) => (
                <th key={col.quantity} scope="col"
                    className={[col.numeric ? 'num' : '',
                                selected.has(keys[i]!) ? 'charted' : '']
                      .filter(Boolean).join(' ') || undefined}>
                  <ColumnHead col={col} k={keys[i]!}
                    selected={selected} onToggle={onToggle} />
                </th>
              ))}
              {several && <th scope="col">Received</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className={several && row.runStart ? 'run-start' : undefined}>
                {several && <th scope="row" className="sensor-cell">{row.sensor}</th>}
                {labelled && <th scope="row">{row.label}</th>}
                {row.cells.map((cell, i) => (
                  <CellView key={columns[i]!.quantity} cell={cell} column={columns[i]!}
                            charted={selected.has(keys[i]!)}
                            receivedAt={row.receivedAt}
                            pollIntervalS={pollIntervalS} />
                ))}
                {several && (() => {
                  // The row's Last Observation, coloured by its freshness
                  // on the first row of each sensor's run (D2).
                  const fresh = row.runStart && judge ? judge(row.receivedAt) : null
                  return (
                    <td className={`received-cell${fresh ? ` fresh-${fresh}` : ''}`}>
                      {row.runStart ? received(row.receivedAt, pollIntervalS, silentAfterS) : null}
                    </td>
                  )
                })()}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** The quantity on one line and its unit on a quieter second, so a column
 *  is named as the gateway names it and the unit never crowds the name. */
function ColumnHead({ col, k, selected, onToggle }: {
  col: Column; k: string; selected: Set<string>; onToggle: (key: string) => void
}) {
  const label = quantityLabel(col.quantity)
  const text = (
    <>
      <span className="col-name">{label}</span>
      {col.unit && <span className="col-unit">{col.unit}</span>}
    </>
  )
  // A text column has no parameter and nothing to chart.
  if (!col.numeric) return text
  const on = selected.has(k)
  return (
    <button type="button" className="col-toggle" aria-pressed={on}
            title={on ? `Remove ${label} from the charts` : `Chart ${label}`}
            onClick={() => onToggle(k)}>
      {text}
    </button>
  )
}

function CellView({ cell, column, charted, receivedAt, pollIntervalS }: {
  cell: Cell | null; column: Column; charted: boolean
  receivedAt: string | null; pollIntervalS: number
}) {
  const mark = charted ? 'charted' : undefined
  if (!cell) return <td className={mark} />
  const note = cellNote(cell, receivedAt, pollIntervalS)
  if (cell.value_type === 'text') {
    return (
      <td className={['text-cell', mark].filter(Boolean).join(' ')}>
        {cell.value ?? '—'}{note && <small className="muted cell-note">{note}</small>}
      </td>
    )
  }
  const value = typeof cell.value === 'number' ? cell.value : null
  return (
    // The exact stored value stays reachable; with a recorded precision the
    // visible one already is it (spec §8.9).
    <td className={['num', mark].filter(Boolean).join(' ')}
        title={value === null ? undefined : String(value)}>
      <span className="cell-value">{displayValue(value, cell.precision)}</span>
      {column.unit === null && cell.unit ? <small className="muted"> {cell.unit}</small> : null}
      {note && <small className="muted cell-note">{note}</small>}
      {cell.quality !== null && isSuspect(cell.quality)
        ? <small className="muted cell-note">{names(cell.quality).join(', ')}</small>
        : null}
    </td>
  )
}
